import { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useToast } from '../components/toast/toast';

interface UseRiskPdfOptions {
  fileName?: string;
  /** Cuando true, usa scale 3 y clase pdf-export-mode (mejor calidad/bordes). Solo Cementos por theme. */
  useImprovedPdfExport?: boolean;
}

export type NodeCaptureStyles = {
  visibility: string;
  transform: string;
  pointerEvents: string;
  left: string;
  top: string;
  position: string;
};

export function saveNodeCaptureStyles(node: HTMLElement): NodeCaptureStyles {
  return {
    visibility: node.style.visibility,
    transform: node.style.transform,
    pointerEvents: node.style.pointerEvents,
    left: node.style.left,
    top: node.style.top,
    position: node.style.position,
  };
}

export function prepareNodeForCapture(node: HTMLElement): void {
  node.style.visibility = 'visible';
  node.style.transform = 'none';
  node.style.pointerEvents = 'none';
  node.style.position = 'fixed';
  node.style.left = '-9999px';
  node.style.top = '0';
}

export function restoreNodeCaptureStyles(
  node: HTMLElement,
  saved: NodeCaptureStyles
): void {
  node.style.visibility = saved.visibility;
  node.style.transform = saved.transform;
  node.style.pointerEvents = saved.pointerEvents;
  node.style.left = saved.left;
  node.style.top = saved.top;
  node.style.position = saved.position;
}

export const useRiskPdf = (options?: UseRiskPdfOptions) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const fileName = options?.fileName || 'analisis_riesgo.pdf';
  const useImprovedPdfExport = options?.useImprovedPdfExport ?? false;

  const generatePdfFromNode = async (node: HTMLElement | null) => {
    if (!node) {
      useToast.error({
        title: 'No se pudo generar el PDF. Intentalo nuevamente.',
        id: 'risk-pdf-error',
        description: '',
        closeButton: true,
        autoClose: 4000,
      });
      return;
    }

    const savedCaptureStyles = saveNodeCaptureStyles(node);
    let spacerDiv: HTMLDivElement | null = null;

    try {
      setIsGenerating(true);

      // PDF dimensions
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const contentWidth = pageWidth - margin * 2;
      const availablePageHeight = pageHeight - margin * 2;

      // Find the page break element
      const pageBreakElement = node.querySelector(
        '[data-pdf-page-break="before"]'
      ) as HTMLElement | null;

      if (pageBreakElement) {
        // Calculate position of page break element relative to the container
        const nodeRect = node.getBoundingClientRect();
        const breakRect = pageBreakElement.getBoundingClientRect();
        const breakPositionInNode = breakRect.top - nodeRect.top;

        // Estimated pixels per PDF page (DOM pixels, before html2canvas scale)
        // A4 at 96dpi ≈ 1123px, minus margins. Tested value that works well with the current layout.
        const PIXELS_PER_PAGE = 1100;

        // Calculate position within the current page
        const positionInCurrentPage = breakPositionInNode % PIXELS_PER_PAGE;

        // Calculate how much whitespace needed to push to next page
        // Add some extra padding (50px) to ensure it's clearly on the next page
        const whitespaceNeededPx = PIXELS_PER_PAGE - positionInCurrentPage + 50;

        // Create spacer div with the calculated height
        spacerDiv = document.createElement('div');
        spacerDiv.id = 'pdf-page-spacer';
        spacerDiv.style.cssText = `
          width: 100% !important;
          height: ${Math.ceil(whitespaceNeededPx)}px !important;
          min-height: ${Math.ceil(whitespaceNeededPx)}px !important;
          background-color: #ffffff !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        `;

        // Insert spacer right before the page break element
        pageBreakElement.parentNode?.insertBefore(spacerDiv, pageBreakElement);

        // Force a reflow to ensure the spacer is rendered
        spacerDiv.offsetHeight;
      }

      if (useImprovedPdfExport) {
        node.classList.add('pdf-export-mode');
        node.offsetHeight;
      }

      prepareNodeForCapture(node);
      node.offsetHeight;

      node.style.backgroundColor = '#ffffff';
      const canvas = await html2canvas(node, {
        scale: useImprovedPdfExport ? 3 : 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: node.scrollWidth,
        windowHeight: node.scrollHeight,
      });
      node.style.backgroundColor = '';

      if (useImprovedPdfExport) {
        node.classList.remove('pdf-export-mode');
      }

      // Remove the spacer from DOM
      if (spacerDiv) {
        spacerDiv.remove();
      }

      // Render canvas to PDF pages
      const scale = contentWidth / canvas.width;
      const totalHeightInMm = canvas.height * scale;
      const pagesNeeded = Math.ceil(totalHeightInMm / availablePageHeight);

      for (let page = 0; page < pagesNeeded; page++) {
        if (page > 0) {
          pdf.addPage();
        }

        const startPixel = page * (availablePageHeight / scale);
        const pixelsForThisPage = Math.min(
          availablePageHeight / scale,
          canvas.height - startPixel
        );

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.ceil(pixelsForThisPage);

        const ctx = pageCanvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(
            canvas,
            0,
            startPixel,
            canvas.width,
            pixelsForThisPage,
            0,
            0,
            pageCanvas.width,
            pageCanvas.height
          );
        }

        const pageImgData = pageCanvas.toDataURL('image/png');
        const pageImgHeight = pageCanvas.height * scale;
        pdf.addImage(pageImgData, 'PNG', margin, margin, contentWidth, pageImgHeight);
      }

      pdf.save(fileName);
    } catch (error) {
      console.error('PDF generation error:', error);
      useToast.error({
        title: 'No se pudo generar el PDF. Intentalo nuevamente.',
        id: 'risk-pdf-error',
        description: '',
        closeButton: true,
        autoClose: 4000,
      });
    } finally {
      restoreNodeCaptureStyles(node, savedCaptureStyles);
      if (spacerDiv?.parentNode) {
        spacerDiv.remove();
      }
      setIsGenerating(false);
    }
  };

  return { isGenerating, generatePdfFromNode };
};
